import { useEffect, useState } from 'react';
import { describeRestore, previewRestore, validateSnapshot, type RestorePreview, type Snapshot } from '../../core/restore';
import { describeChange } from '../../core/audit';
import type { UserRecord } from '../../data/auth';
import { downloadBackup, performRestore, readLive } from '../../data/backup';
import { recomputeMonth } from '../../data/rollups';
import { watchAccounts } from '../../data/accounts';
import type { Account } from '../../core/accounts';

interface Props {
  record: UserRecord;
}

/**
 * Backup and restore (FR-BAK-03, FR-BAK-05, FR-BAK-09).
 *
 * Restoring is the most destructive thing the app can do — it writes older records over
 * live ones — so the screen is built around making the user look first. The preview is
 * produced by the same function that computes the writes, which is the only reason it can
 * be trusted.
 */
export function BackupScreen({ record }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const [live, setLive] = useState<Record<string, Record<string, Record<string, unknown>>> | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchAccounts(setAccounts), []);

  async function download() {
    setError(null);
    setBusy('download');
    try {
      const { fileName, counts } = await downloadBackup();
      const total = Object.values(counts).reduce((n, c) => n + Math.max(0, c), 0);
      setNotice(`Downloaded ${fileName} — ${total} records across ${Object.keys(counts).length} collections.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not build the backup.');
    } finally {
      setBusy(null);
    }
  }

  async function choose(file: File) {
    setError(null);
    setNotice(null);
    setConfirming(false);
    setBusy('preview');

    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      const { snapshot: valid, problem } = validateSnapshot(parsed);
      if (!valid) {
        setError(problem ?? 'That file could not be read.');
        setSnapshot(null);
        setPreview(null);
        return;
      }

      // Read once and used for both the preview and the write, so the two cannot
      // disagree about what was there.
      const current = await readLive();
      setLive(current);
      setSnapshot(valid);
      setPreview(previewRestore(valid, current));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That file is not valid JSON.');
    } finally {
      setBusy(null);
    }
  }

  async function restore() {
    if (!snapshot || !live || !preview) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }

    setError(null);
    setBusy('restore');
    try {
      const result = await performRestore(snapshot, live, record.uid);

      // Rollups are not restored — they are a cache derived from the transactions — so
      // the months just written are rebuilt now rather than left to the nightly job.
      // Leaving them would mean a wrong dashboard until tomorrow.
      const accountType = (id: string) => accounts.find((a) => a.id === id)?.type;
      for (const monthKey of result.monthsToRebuild) {
        await recomputeMonth(monthKey, accountType);
      }

      setNotice(
        `Restored ${result.written} record${result.written === 1 ? '' : 's'}` +
          (result.monthsToRebuild.length > 0
            ? `, and rebuilt ${result.monthsToRebuild.length} month${result.monthsToRebuild.length === 1 ? '' : 's'} of totals.`
            : '.'),
      );
      setSnapshot(null);
      setPreview(null);
      setConfirming(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The restore failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="head-row">
        <h1>Backup and restore</h1>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <section className="panel">
        <h2>Download a snapshot</h2>
        <p className="muted tiny">
          Timestamped JSON of every collection (FR-BAK-04). The weekly automatic backup
          runs as a scheduled job and writes to Firebase Storage and, once the one-time
          consent is done, to Google Drive — see <code>server/README.md</code>.
        </p>
        <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => void download()}>
          {busy === 'download' ? 'Building…' : 'Download now'}
        </button>
      </section>

      <section className="panel">
        <h2>Restore from a snapshot</h2>
        <p className="muted tiny">
          Nothing is written until you have seen what would change. A restore{' '}
          <strong>never deletes</strong>: records created since the backup are left alone.
        </p>

        <label className="btn">
          Choose a backup file
          <input
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => e.target.files?.[0] && void choose(e.target.files[0])}
          />
        </label>
        {busy === 'preview' && <p className="muted tiny">Comparing against what is here now…</p>}
      </section>

      {preview && (
        <section className="panel">
          <h2>What would change</h2>
          <p className="muted tiny">
            Backup taken {new Date(preview.takenAt).toLocaleString()}.
          </p>

          <p className={confirming ? 'warn' : undefined}>{describeRestore(preview)}</p>

          {preview.problems.length > 0 && (
            <ul className="muted tiny skip-reasons">
              {preview.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}

          {preview.totalWrites > 0 && (
            <div className="table-scroll">
              <table className="table reclassify-table">
                <thead>
                  <tr>
                    <th>Collection</th>
                    <th className="num">Restored</th>
                    <th className="num">Overwritten</th>
                    <th className="num">Already match</th>
                    <th className="num">Left alone</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.collections
                    .filter((c) => c.added + c.changed > 0)
                    .map((c) => (
                      <tr key={c.collection}>
                        <td>{c.collection}</td>
                        <td className="num">{c.added || '—'}</td>
                        <td className="num">{c.changed || '—'}</td>
                        <td className="num muted">{c.identical || '—'}</td>
                        <td className="num muted">{c.untouched || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Field-level samples, so "overwritten" is something the user can judge rather
              than a number they have to trust. */}
          {preview.collections.some((c) => c.samples.length > 0) && (
            <>
              <h3 className="tiny">Examples</h3>
              <ul className="change-list muted tiny">
                {preview.collections.flatMap((c) =>
                  c.samples.map((sample) => (
                    <li key={`${c.collection}-${sample.id}`}>
                      <strong>{sample.label}</strong> · {sample.kind}
                      {sample.changes.length > 0 && ` — ${sample.changes.map(describeChange).join('; ')}`}
                    </li>
                  )),
                )}
              </ul>
            </>
          )}

          {preview.totalWrites > 0 && (
            <div className="row">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy !== null}
                onClick={() => void restore()}
              >
                {busy === 'restore'
                  ? 'Restoring…'
                  : confirming
                    ? `Yes — write ${preview.totalWrites} record${preview.totalWrites === 1 ? '' : 's'}`
                    : `Restore ${preview.totalWrites} record${preview.totalWrites === 1 ? '' : 's'}`}
              </button>
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() => {
                  setSnapshot(null);
                  setPreview(null);
                  setConfirming(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {confirming && (
            <p className="warn tiny">
              This is written to the audit log (FR-BAK-09), and the monthly totals for any
              months affected are rebuilt straight afterwards.
            </p>
          )}
        </section>
      )}
    </>
  );
}
